import { Box, CloseButton, Link, Stack, Text, ThemingProps, useMultiStyleConfig } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { useCustomerCopy } from '../hooks/tenant/useCustomerCopy';

type Props = Pick<ThemingProps<'NewsBanner'>, 'colorScheme'>;

/**
 * Dismissible announcement bar rendered under the header. Its content is the `newsBanner.*` copy
 * slots, so staff publish or change it from the admin theme screen without a deploy; with no body
 * set it renders nothing. Setting a title switches to the two-line `titled` variant.
 */
export const NewsBanner = ({ colorScheme }: Props) => {
  const copy = useCustomerCopy();
  const title = copy('newsBanner.title');
  const body = copy('newsBanner.body');
  const linkText = copy('newsBanner.linkText');
  const linkUrl = copy('newsBanner.linkUrl');

  // Dismissal is remembered against the content itself, so publishing a new announcement re-shows
  // the banner to users who closed the previous one.
  const contentKey = useMemo(() => JSON.stringify([title, body, linkText, linkUrl]), [title, body, linkText, linkUrl]);
  const [dismissedKey, setDismissedKey] = useLocalStorage<string>('dismissedNewsBanner', '');

  const hasTitle = !!title;
  const showBanner = !!body && dismissedKey !== contentKey;
  const showLink = !!linkText && !!linkUrl;
  const isExternalLink = /^https?:\/\//.test(linkUrl);

  const styles = useMultiStyleConfig('NewsBanner', { colorScheme, variant: hasTitle ? 'titled' : 'inline' });

  return (
    <Show if={showBanner}>
      <Stack role="status" direction="row" __css={styles.container}>
        <Box __css={styles.content}>
          <Show if={hasTitle}>
            <Text sx={styles.title}>{title}</Text>
          </Show>
          <Text sx={styles.body}>
            {body}
            <Show if={showLink}>
              {' '}
              <Link href={linkUrl} isExternal={isExternalLink} sx={styles.link}>
                {linkText}
              </Link>
            </Show>
          </Text>
        </Box>
        <CloseButton
          size="sm"
          aria-label="Dismiss announcement"
          sx={styles.closeButton}
          onClick={() => setDismissedKey(contentKey)}
        />
      </Stack>
    </Show>
  );
};
